import { type TimelineFeatures, useFeatures } from "@expert/features";
import { trackFullStoryEvent } from "@expert/monitoring";
import { HelpMeNowButton, NewMessageButton, ProactiveSuggestionButton } from "@expert/proactive-notifications";
import { Group } from "@mantine/core";
import { useReactAnalytics } from "@soluto-private/eventualize-react";
import { useSendExpertAssistMessage } from "../../hooks";
import type { ExpertAssistMessage } from "../../shared-types";
import { useTimelineStore } from "../../state";
import { isFromBot, useAutoscroll } from "../../utils";
import type { TimelineListProps } from "./types";

interface TimelineActionGroupProps extends TimelineListProps {
    proactiveSuggestions: string[];
    shouldShowNewMessageButton: boolean;
    unreadMessages: number;
}
export function TimelineActionGroup({
    gaiaPayloadProps,
    isExternal,
    logger,
    scrollRef,
    shouldShowNewMessageButton,
    unreadMessages,
    proactiveSuggestions,
}: TimelineActionGroupProps) {
    const { dispatcher } = useReactAnalytics();
    const {
        features: { helpMeNowQuestion, isHelpMeNowEnabled, isProactiveSuggestionEnabled },
    } = useFeatures<TimelineFeatures>();
    const { start: startAutoscroll } = useAutoscroll(scrollRef);
    const sendMessage = useSendExpertAssistMessage(logger, isExternal, gaiaPayloadProps);
    const { currentSessionId: sessionId, getTimelineState, setNewSuggestions } = useTimelineStore();
    const { callSid, chatId, messages } = getTimelineState(sessionId);

    const mostRecentMessage = messages[messages.length - 1] as ExpertAssistMessage | undefined;
    const isLoadingBotMessage = !isFromBot(mostRecentMessage?.type ?? "Default");

    const shouldShowHelpMeNow = (!!callSid || !!chatId) && isHelpMeNowEnabled;

    const onNewMessageClick = () => {
        logger.info("New message button clicked");
        void dispatcher.dispatchUserAction("Click", "NewMessage", { sessionId });
        startAutoscroll();
    };

    const onHelpMeNowClick = () => {
        logger.info("Help me now button clicked");
        const type = "Expert";
        const button = "Quick solve";
        const messageId = crypto.randomUUID();
        void dispatcher.dispatchBusinessEvent("Click_HelpMeNow", { button, sessionId, messageId });
        trackFullStoryEvent("Click_HelpMeNow", {
            button,
            sessionId,
            callSid,
            chatId,
            messageId,
        });
        sendMessage({
            text: helpMeNowQuestion,
            type,
            id: messageId,
            metadata: { helpMeNowText: button },
            source: "HelpMeNow",
        });
    };

    const onProactiveSuggestionClick = (suggestion: string) => {
        logger.info({ suggestion }, "Proactive suggestion button clicked");
        const suggestionIndex = proactiveSuggestions.indexOf(suggestion);
        const type = "Expert";
        const messageId = crypto.randomUUID();
        void dispatcher.dispatchBusinessEvent("Click_ProactiveSuggestionButton", {
            button: "ProactiveSuggestion",
            suggestion,
            suggestionIndex,
            messageId,
            sessionId,
        });
        trackFullStoryEvent("Click_ProactiveSuggestionButton", {
            button: "ProactiveSuggestion",
            suggestion,
            suggestionIndex,
            callSid,
            chatId,
            messageId,
            sessionId,
        });
        sendMessage({
            id: messageId,
            source: "ProactiveSuggestion",
            text: suggestion,
            type,
        });
        setNewSuggestions(sessionId, []);
    };

    const shouldShowProactiveSuggestions = isProactiveSuggestionEnabled && proactiveSuggestions.length > 0 && !!callSid;

    return (
        <Group
            pos="sticky"
            align="center"
            dir="row"
            justify="justify-content"
            bg="transparent"
            gap="xxs"
            p="0"
            px="md"
            pb="sm"
        >
            <NewMessageButton
                shouldShow={shouldShowNewMessageButton}
                shouldCollapseIfNeeded={shouldShowProactiveSuggestions}
                num={unreadMessages}
                onClick={onNewMessageClick}
            />
            <HelpMeNowButton
                shouldShow={shouldShowHelpMeNow}
                shouldCollapseIfNeeded={shouldShowProactiveSuggestions}
                isLoading={isLoadingBotMessage}
                onClick={onHelpMeNowClick}
            />
            {shouldShowProactiveSuggestions &&
                proactiveSuggestions
                    .slice(0, 2)
                    .map((suggestion) => (
                        <ProactiveSuggestionButton
                            key={suggestion}
                            suggestion={suggestion}
                            onClick={onProactiveSuggestionClick}
                            shouldShow={shouldShowProactiveSuggestions}
                        />
                    ))}
        </Group>
    );
}
